import React, { useEffect, useRef, useState } from 'react';
import proptypes from 'prop-types';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  Typography,
  RadioGroup,
  SelectionControlLabel,
  Radio,
  Checkbox
} from '@one-thd/sui-atomic-components';
import { useFormContext, Controller } from 'react-hook-form';
import { ShareProjectDescription } from '../ShareProjectDescription';
import { EntireProject } from '../EntireProject';
import { GroupProject } from '../GroupProject';
import { isWorkspaceDashboard, triggerAnalytics } from '../../helpers';
import {
  SHARE_YOUR_PROJECT_STEP,
  STR_1,
  CANCEL_BUTTON,
  X_BUTTON,
  SHARE_YOUR_PROJECT_CONTINUE,
  PROJECTS_INTERACTION
} from '../../constants';

const {
  func,
  shape,
  string,
  arrayOf,
  number
} = proptypes;

// Visual gap between the scrolled-to error and the sticky footer, on top of the
// footer's measured height. One design-system spacing unit (sui-*-4 = 1rem).
const FOOTER_GAP_PX = 16;

export const ShareDetails = ({ onClose, onContinue, itemGroups, itemCount }) => {
  const isInWorkspaceDashboard = isWorkspaceDashboard();
  const {
    trigger,
    control,
    watch,
    setValue,
    formState: { errors: formErrors },
    getValues
  } = useFormContext();
  const { whatShared } = watch();

  // Static anchor at the bottom of the drawer content. Error messages are
  // conditionally rendered, so we scroll to this always-present element to
  // guarantee a consistent scroll target regardless of which error shows.
  const scrollAnchorRef = useRef(null);
  const footerRef = useRef(null);
  // Bumped on every invalid Continue press so the scroll fires each time, even
  // when the same errors are already showing (the effect runs after the error
  // DOM commits, so the anchor is at its final position).
  const [scrollNonce, setScrollNonce] = useState(0);

  useEffect(() => {
    if (scrollNonce === 0 || !scrollAnchorRef.current) {
      return;
    }
    // The DrawerFooter is sticky-positioned over the bottom of the scroll
    // container, so reserve its height as scroll margin to keep the anchor
    // (and the error above it) from landing behind it.
    const footerHeight = footerRef.current?.offsetHeight || 0;
    scrollAnchorRef.current.style.scrollMarginBottom = `${footerHeight + FOOTER_GAP_PX}px`;
    scrollAnchorRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [scrollNonce]);

  const groupCount = itemGroups?.length || 0;

  const getGroupsItemsShared = (groupsShared) => {
    let itemsShared = 0;
    const groupsMap = new Map();
    itemGroups.forEach((group) => {
      groupsMap.set(group.id, group.products.length);
    });
    groupsShared.forEach((id) => {
      itemsShared += groupsMap.get(id) || 0;
    });
    return [groupsShared.length, itemsShared];
  };

  const getAnalyticsPayload = () => {
    const values = getValues();
    const pricing = `pricing-${values.pricingChoice === 'yes' ? 'show' : 'hide'}`;
    if (values.whatShared === 'entire-project') {
      return { shared: `project-${groupCount}-${itemCount}: ${pricing}` };
    }
    const [groups, items] = getGroupsItemsShared(values.groupsShared);
    return { shared: `group-${groups}-${items}: ${pricing}` };
  };

  const getWorkspaceDashboardAnalyticsPayload = () => {
    const values = getValues();
    const pricing = values.pricingChoice === 'yes' ? 'share' : 'hide';
    const action = 'share-overlay-button-click';

    if (values.whatShared === 'entire-project') {
      return {
        action,
        settings: `homepage: workspace dashboard: projects: share continue: entire|${pricing}`
      };
    }

    const unsortedMaterialsGroup = itemGroups.find((group) => group.isDefault);
    const selectedGroups = values.groupsShared || [];
    const isUnsortedMaterialsSelected = unsortedMaterialsGroup
      ? selectedGroups.includes(unsortedMaterialsGroup.id)
      : false;
    const options = `group,${isUnsortedMaterialsSelected ? 'yes' : 'no'}`;

    return {
      action,
      settings: `homepage: workspace dashboard: projects: share continue: ${options}|${pricing}`
    };
  };

  const handleOnContinue = async () => {
    const valid = await trigger(['whatShared', 'groupsShared', 'pricingChoice']);
    if (!valid) {
      setScrollNonce((nonce) => nonce + 1);
      return;
    }

    const analyticsEvent = isInWorkspaceDashboard ? PROJECTS_INTERACTION : SHARE_YOUR_PROJECT_CONTINUE;
    const analyticsPayload = isInWorkspaceDashboard ? getWorkspaceDashboardAnalyticsPayload() : getAnalyticsPayload();
    triggerAnalytics(analyticsEvent, analyticsPayload);
    onContinue();
  };

  const handleOnClose = (action) => {
    triggerAnalytics(SHARE_YOUR_PROJECT_STEP, { step: STR_1, action });
    onClose();
  };

  return (
    <div data-testid="share-details">
      <DrawerHeader title="Share Your Project" onClose={() => handleOnClose(X_BUTTON)} />
      <DrawerBody>
        <div className="sui-gap-6 sui-flex sui-flex-col">
          <ShareProjectDescription />
          <div className="sui-gap-2 sui-flex sui-flex-col">
            <Typography variant="h5">Share Options</Typography>
            <Typography id="share-options-radio-buttons-group-label">
              What do you want to share?
            </Typography>
            <Controller
              name="whatShared"
              control={control}
              render={({ field }) => {
                const { value, name, onChange } = field;
                return (
                  <RadioGroup
                    onChange={(_event, val) => {
                      if (val === 'entire-project') {
                        setValue('groupsShared', []);
                      }
                      onChange(val);
                    }}
                    aria-labelledby="share-options-radio-buttons-group-label"
                    name={name}
                    value={value}
                  >
                    <SelectionControlLabel
                      value="entire-project"
                      label={<EntireProject itemCount={itemCount} groupCount={groupCount} />}
                    >
                      <Radio />
                    </SelectionControlLabel>
                    <SelectionControlLabel value="groups" label="Groups">
                      <Radio />
                    </SelectionControlLabel>
                  </RadioGroup>
                );
              }}
            />

            {!!itemGroups.length && (
              <div className="sui-ml-6 sui-flex sui-flex-col">
                <Controller
                  name="groupsShared"
                  control={control}
                  render={({ field }) => {
                    const { value, onChange } = field;
                    const setOfSelectedGroups = new Set(value);
                    return (
                      <>
                        {itemGroups.map((group) => (
                          <SelectionControlLabel
                            key={group.id}
                            label={(
                              <GroupProject
                                itemCount={group.products.length}
                                groupName={group.isDefault ? 'Unsorted Materials' : group.groupName}
                                disabled={whatShared !== 'groups'}
                              />
                            )}
                          >
                            <Checkbox
                              checked={setOfSelectedGroups.has(group.id)}
                              disabled={whatShared !== 'groups'}
                              onChange={() => {
                                if (setOfSelectedGroups.has(group.id)) {
                                  setOfSelectedGroups.delete(group.id);
                                } else {
                                  setOfSelectedGroups.add(group.id);
                                }

                                onChange([...setOfSelectedGroups.values()]);
                              }}
                              value={group.id}
                              name={group.id}
                            />
                          </SelectionControlLabel>
                        ))}
                      </>
                    );
                  }}
                />
              </div>
            )}
            {(formErrors.whatShared?.message || formErrors.groupsShared?.message) && (
              <Alert status="error">
                {formErrors.whatShared?.message || formErrors.groupsShared?.message}
              </Alert>
            )}
          </div>
          <hr />
          <div className="sui-gap-2 sui-flex sui-flex-col">
            <Typography variant="h5">Do you want to share pricing?</Typography>
            <Typography id="pricing-radio-buttons-group-label">
              Choose to share or hide retail prices of the individual products you are sharing. Your
              Estimated Total, Quote price, and discounts will never be shared.
            </Typography>
            <Controller
              name="pricingChoice"
              control={control}
              render={({ field }) => {
                const { onChange, value } = field;
                return (
                  <RadioGroup
                    onChange={(_event, val) => {
                      onChange(val);
                    }}
                    name="pricing-radio-buttons-group"
                    aria-labelledby="pricing-radio-buttons-group-label"
                    value={value}
                  >
                    <SelectionControlLabel value="yes" label="Yes, share pricing">
                      <Radio />
                    </SelectionControlLabel>
                    <SelectionControlLabel value="no" label="No, hide pricing">
                      <Radio />
                    </SelectionControlLabel>
                  </RadioGroup>
                );
              }}
            />
            {formErrors.pricingChoice?.message && (
              <Alert status="error">
                {formErrors.pricingChoice.message}
              </Alert>
            )}
          </div>
        </div>
        <div ref={scrollAnchorRef} aria-hidden="true" />
      </DrawerBody>
      <DrawerFooter ref={footerRef}>
        <div className="sui-flex sui-flex-col sui-gap-4">
          <Button fullWidth onClick={handleOnContinue} data-testid="share-details-continue-button">
            Continue
          </Button>
          <Button fullWidth variant="secondary" onClick={() => handleOnClose(CANCEL_BUTTON)}>
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </div>
  );
};

ShareDetails.propTypes = {
  onClose: func.isRequired,
  onContinue: func.isRequired,
  itemCount: number.isRequired,
  itemGroups: arrayOf(
    shape({ id: string, groupName: string, products: arrayOf(shape({ id: string })) })
  ).isRequired
};

ShareDetails.defaultProps = {};
